import { Dispatch, SetStateAction, useState } from 'react'
import { UseFormSetError } from 'react-hook-form'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import {
  CountryCode,
  CustomerType,
  PrefectureCode,
  useCustomerUpdateMutation,
  useCustomerQuery,
} from 'lib/graphql/storefront'
import { handleApolloError } from 'config/helpers'

export interface CustomerForm {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  corporationName?: string
  customerType?: CustomerType
  address?: {
    prefectureCode?: PrefectureCode
    zip?: string
    city?: string
    address1?: string
    address2?: string
  }
  bookableFlag?: boolean
}

type ErrorFieldName =
  | 'phone'
  | 'email'
  | 'address'
  | 'firstName'
  | 'lastName'
  | 'corporationName'
  | 'address.prefectureCode'
  | 'address.zip'
  | 'address.city'
  | 'address.address1'
  | 'address.address2'

// TODO: define correct props type
const useUserUpdate = (
  setError: UseFormSetError<CustomerForm>,
  setAlertMessage: Dispatch<SetStateAction<string>>
) => {
  const { t } = useTranslation()
  const [onUpdate] = useCustomerUpdateMutation({})
  const { data: customerData } = useCustomerQuery({})
  const [updating, setUpdating] = useState<boolean>(false)
  const [phoneUsed, setPhoneUsed] = useState<string | null>(null)
  const [emailUsed, setEmailUsed] = useState<string | null>(null)
  const [updateInfo, setUpdateInfo] = useState<null | CustomerForm>(null)
  const currentEmail = customerData?.customer?.email

  // handle submit
  const onSubmit = async (formValue: CustomerForm) => {
    // emailが変更されていたら updateAuth0User を true にする
    const isChangeEmail = formValue.email !== currentEmail

    try {
      setUpdating(true)

      const res = await onUpdate({
        variables: {
          updateAuth0User: isChangeEmail,
          input: {
            email: formValue.email,
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            corporationName: formValue.corporationName || null,
            type: formValue.customerType,
            phone: formValue.phone,
            address: {
              ...formValue.address,
              countryCode: CountryCode.Jpn,
            },
            bookableFlag: customerData?.customer?.bookableFlag ?? false,
          },
          allowDuplicate: true,
        },
      })
      const { customer, customerUserErrors } = res.data?.customerUpdate
      if (customer) {
        setUpdating(false)
        setUpdateInfo(formValue)
      } else if (customerUserErrors.length) {
        customerUserErrors.forEach((value) => {
          const { field, code } = value
          let fieldMessage = ''
          switch (code) {
            case 'TAKEN':
              fieldMessage = t('errors.contact-info-error-TAKEN')
              if (field.includes('phone')) {
                setPhoneUsed(formValue.phone)
              }
              if (field.includes('email')) {
                setEmailUsed(formValue.email)
              }
              break
            case 'INVALID':
              fieldMessage = t('errors.contact-info-error-INVALID')
              break
            case 'BLANK':
              fieldMessage = t('errors.contact-info-error-BLANK')
              break
            case 'TOO_SHORT':
              fieldMessage = t('errors.contact-info-error-TOO_SHORT')
              break
            case 'TOO_LONG':
              fieldMessage = t('errors.contact-info-error-TOO_LONG')
              break
            case 'CONTAINS_HTML_TAGS':
              fieldMessage = t('errors.contact-info-error-CONTAINS_HTML_TAGS')
              break
            case 'CONTAINS_URL':
              fieldMessage = t('errors.contact-info-error-CONTAINS_URL')
              break
            // TODO: 以下のようなエラーがあったらこの画面にリダイレクトできないはずですが
            case 'TOKEN_INVALID':
              setAlertMessage(t('errors.contact-info-error-TOKEN_INVALID'))
              break
            case 'UNDEFINED_CUSTOMER':
              setAlertMessage(t('errors.contact-info-error-UNDEFINED_CUSTOMER'))
              break
            case 'AUTH0_EMAIL_NOT_FOUND':
              setAlertMessage(
                t('errors.contact-info-error-AUTH0_EMAIL_NOT_FOUND')
              )
              break
            case 'AUTH0_INVALID_URL':
              setAlertMessage(t('errors.contact-info-error-AUTH0_INVALID_URL'))
              break
            case 'BAD_DOMAIN':
              setAlertMessage(t('errors.contact-info-error-BAD_DOMAIN'))
              break
            case 'CUSTOMER_DISABLED':
              setAlertMessage(t('errors.contact-info-error-CUSTOMER_DISABLED'))
              break
            case 'NOT_FOUND':
              setAlertMessage(t('errors.contact-info-error-NOT_FOUND'))
              break
            default:
              setAlertMessage(value.message)
              break
          }
          // concat field name if address is included
          const fieldName = field.includes('address')
            ? `${field[0]}.${field[1]}`
            : field[0]
          //　graphqlの戻り値のerrorMessageを表示
          setError(fieldName as ErrorFieldName, {
            type: 'serverResponse',
            message: fieldMessage,
          })
        })
      }
    } catch (e) {
      setAlertMessage(handleApolloError(e))
    } finally {
      setUpdating(false)
    }
  }

  return {
    updateInfo,
    updating,
    phoneUsed,
    emailUsed,
    onSubmit,
  }
}

export default useUserUpdate
